const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

function isInternalNavigableLink(anchor) {
  if (!(anchor instanceof HTMLAnchorElement)) return false;
  if (!anchor.href || anchor.target === '_blank' || anchor.hasAttribute('download')) return false;

  const url = new URL(anchor.href, window.location.href);

  if (url.origin !== window.location.origin) return false;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return false;
  if (anchor.closest('[aria-haspopup="dialog"], cart-drawer-component, search-modal')) return false;

  return true;
}

function markPageReady() {
  window.requestAnimationFrame(() => {
    document.body.classList.add('thegee-page-ready');
    document.body.classList.remove('thegee-page-leaving');
  });
}

function initPageTransitions() {
  if (document.body.dataset.thegeeTransitionsBound === 'true') return;
  document.body.dataset.thegeeTransitionsBound = 'true';

  markPageReady();
  window.addEventListener('pageshow', markPageReady);

  if (REDUCED_MOTION.matches) return;

  document.addEventListener('click', (event) => {
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor || !isInternalNavigableLink(anchor)) return;
    if (document.body.classList.contains('thegee-page-leaving')) return;

    event.preventDefault();
    document.body.classList.add('thegee-page-leaving');
    document.body.classList.remove('thegee-page-ready');

    window.setTimeout(() => {
      window.location.href = anchor.href;
    }, 500);
  });
}

function bindPositionalZoom(container, image) {
  if (!container || !image || container.dataset.thegeeZoomBound === 'true') return;
  container.dataset.thegeeZoomBound = 'true';

  container.addEventListener('mousemove', (event) => {
    const rect = container.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    image.style.transformOrigin = `${clampedX}% ${clampedY}%`;
  });

  container.addEventListener('mouseleave', () => {
    window.setTimeout(() => {
      image.style.transformOrigin = '50% 50%';
    }, 400);
  });
}

function ensureGalleryIndicators(gallery, count) {
  let container = gallery.querySelector('.thegee-card-indicators');

  if (!container) {
    container = document.createElement('div');
    container.className = 'thegee-card-indicators';
    gallery.appendChild(container);
  }

  if (container.children.length !== count) {
    container.innerHTML = '';
    for (let index = 0; index < count; index += 1) {
      const dot = document.createElement('div');
      dot.className = 'thegee-card-indicator';
      container.appendChild(dot);
    }
  }

  return Array.from(container.children);
}

function wireCardGalleries() {
  document.querySelectorAll('.card-gallery[data-thegee-hover-mode]').forEach((gallery) => {
    if (gallery.dataset.thegeeBound === 'true') return;
    gallery.dataset.thegeeBound = 'true';

    const mode = gallery.dataset.thegeeHoverMode;
    const image = gallery.querySelector('.product-media__image');

    if (mode === 'zoom' && image) {
      bindPositionalZoom(gallery, image);
      return;
    }

    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || REDUCED_MOTION.matches) return;

    const visibleSlides = Array.from(slideshow.querySelectorAll('slideshow-slide:not([hidden])'));
    if (visibleSlides.length < 2) return;

    const indicators = ensureGalleryIndicators(gallery, visibleSlides.length);
    let currentIndex = 0;
    let intervalId = null;

    const updateIndicators = (index) => {
      indicators.forEach((indicator, indicatorIndex) => {
        indicator.classList.toggle('is-active', indicatorIndex === index);
      });
    };

    const select = (index, animate = true) => {
      currentIndex = (index + visibleSlides.length) % visibleSlides.length;
      if (typeof slideshow.select === 'function') {
        slideshow.select({ id: visibleSlides[currentIndex].getAttribute('slide-id') }, undefined, { animate });
      }
      updateIndicators(currentIndex);
    };

    gallery.addEventListener('slideshow:select', (event) => {
      if (!event.detail) return;
      currentIndex = visibleSlides.findIndex((slide) => slide.getAttribute('slide-id') === event.detail.id);
      if (currentIndex < 0) return;
      updateIndicators(currentIndex);
    });

    const start = () => {
      if (intervalId) return;
      intervalId = window.setInterval(() => {
        select(currentIndex + 1);
      }, 1200);
    };

    const stop = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      select(0, false);
    };

    updateIndicators(0);
    gallery.addEventListener('mouseenter', start);
    gallery.addEventListener('mouseleave', stop);
    gallery.addEventListener('focusin', start);
    gallery.addEventListener('focusout', stop);
  });
}

function wireBestSellerMedia() {
  document.querySelectorAll('.thegee-home-showcase__product-media[data-thegee-hover-mode]').forEach((media) => {
    if (media.dataset.thegeeBound === 'true') return;
    media.dataset.thegeeBound = 'true';

    const mode = media.dataset.thegeeHoverMode;
    const slides = Array.from(media.querySelectorAll('.thegee-home-showcase__product-slide'));
    const indicators = Array.from(media.querySelectorAll('.thegee-home-showcase__product-indicator'));

    const setActive = (index) => {
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });
      indicators.forEach((indicator, indicatorIndex) => {
        indicator.classList.toggle('is-active', indicatorIndex === index);
      });
    };

    setActive(0);

    if (mode === 'zoom' && slides[0]) {
      bindPositionalZoom(media, slides[0]);
      return;
    }

    if (slides.length < 2 || REDUCED_MOTION.matches) return;

    let index = 0;
    let intervalId = null;
    const trigger = media.closest('a');

    const start = () => {
      if (intervalId) return;
      intervalId = window.setInterval(() => {
        index = (index + 1) % slides.length;
        setActive(index);
      }, 1200);
    };

    const stop = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      index = 0;
      setActive(index);
    };

    trigger?.addEventListener('mouseenter', start);
    trigger?.addEventListener('mouseleave', stop);
    media.addEventListener('focusin', start);
    media.addEventListener('focusout', stop);
  });
}

function wireProductZoom() {
  document
    .querySelectorAll('.product-information[data-thegee-product-mode="jewelry"] .product-media-container')
    .forEach((container) => {
      const image = container.querySelector('.product-media__image');
      if (image) bindPositionalZoom(container, image);
    });
}

function initTheGeeOriginalUpgrade() {
  wireCardGalleries();
  wireBestSellerMedia();
  wireProductZoom();
}

document.addEventListener('DOMContentLoaded', () => {
  initPageTransitions();
  initTheGeeOriginalUpgrade();
});

document.addEventListener('shopify:section:load', () => {
  initTheGeeOriginalUpgrade();
});
